import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Cookie disclosure banner for the public site.
 *
 * Presentational only — the parent (PublicSite) owns the persisted choice and
 * decides whether to render this. That keeps the "did the visitor consent?"
 * logic in one place so non-essential trackers (Google reCAPTCHA) can be gated
 * on it.
 *
 * Outcomes wired by the parent:
 *   onAccept()  → optional cookies allowed; reCAPTCHA may load. Persisted.
 *   onDecline() → optional cookies stay off; reCAPTCHA never loads. Persisted.
 *   onDismiss() → closes for this visit WITHOUT recording consent (Law 25:
 *                 dismissal is not consent), so the banner returns next visit.
 */
const CookieConsent = ({ onAccept, onDecline, onDismiss }) => {
    const { t } = useTranslation();

    return (
        <div
            className="cookie-consent"
            role="dialog"
            aria-modal="false"
            aria-labelledby="cookie-consent-title"
            aria-describedby="cookie-consent-desc"
        >
            <div className="cookie-consent-inner">
                <button
                    type="button"
                    className="cookie-consent-close"
                    aria-label={t('public.cookieConsent.dismiss')}
                    onClick={onDismiss}
                >
                    &times;
                </button>
                <div className="cookie-consent-text">
                    <b id="cookie-consent-title">{t('public.cookieConsent.title')}</b>
                    <p id="cookie-consent-desc">
                        {t('public.cookieConsent.message')}{' '}
                        <Link to="/privacy" className="cookie-consent-link">
                            {t('public.cookieConsent.learnMore')}
                        </Link>
                    </p>
                </div>
                <div className="cookie-consent-actions">
                    <button
                        type="button"
                        className="cookie-btn cookie-btn-decline"
                        onClick={onDecline}
                    >
                        {t('public.cookieConsent.decline')}
                    </button>
                    <button
                        type="button"
                        className="cookie-btn cookie-btn-accept"
                        onClick={onAccept}
                    >
                        {t('public.cookieConsent.accept')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CookieConsent;
